/**
 * DialogoInvito — crea il link di una prova (atlante cap. 489): per chi e per quanti giorni, poi il link da copiare e il suo
 * codice QR, da mostrare a chi è davanti a te.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { creaInvito, motivoDellErrore, type Prova } from '../../services/proveService';
import { useFormati } from '../partner/formati';
import { tokens } from '../../theme';

/** La proposta del cap. 489: il server accetta da 1 a 90 giorni. */
const GIORNI_PROPOSTI = 14;

export const DialogoInvito: React.FC<{
  prova: Prova;
  onChiudi: () => void;
  onCreato: () => void;
  onCopia: (link: string) => void;
}> = ({ prova, onChiudi, onCreato, onCopia }) => {
  const { t } = useTranslation('prove');
  const f = useFormati();
  const [nome, setNome] = useState('');
  const [giorni, setGiorni] = useState(String(GIORNI_PROPOSTI));
  const [pubblico, setPubblico] = useState(false);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [creato, setCreato] = useState<{ link: string; scade_il: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (!creato) return undefined;
    let attivo = true;
    QRCode.toDataURL(creato.link, { margin: 1, width: 220 })
      .then((immagine) => { if (attivo) setQr(immagine); })
      .catch((e: unknown) => console.error('[prove] codice QR non creato', e));
    return () => { attivo = false; };
  }, [creato]);

  const crea = async () => {
    setInvio(true);
    setErrore(null);
    try {
      const r = await creaInvito({ prova_id: prova.id, nome: nome.trim(), giorni: Number(giorni) || GIORNI_PROPOSTI, pubblico });
      setCreato({ link: r.link, scade_il: r.scade_il });
      onCreato();
    } catch (e) {
      const motivo = motivoDellErrore(e);
      setErrore(motivo ? t(`motivi.${motivo}`) : t('messaggi.errore_generico'));
    } finally {
      setInvio(false);
    }
  };

  return (
    <Dialog open onClose={invio ? undefined : onChiudi} fullWidth maxWidth="xs">
      <DialogTitle>{t('dialogo_invito.titolo', { prova: prova.etichetta })}</DialogTitle>
      <DialogContent>
        {creato ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: tokens.color.ash }}>
              {t('dialogo_invito.pronto', { giorno: f.giorno(creato.scade_il.slice(0, 10)) })}
            </Typography>
            {qr && (
              <Box sx={{ textAlign: 'center' }}>
                <img src={qr} alt={t('dialogo_invito.qr')} width={220} height={220} />
              </Box>
            )}
            <TextField value={creato.link} fullWidth multiline size="small" InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {errore && <Alert severity="error">{errore}</Alert>}
            <TextField label={t('dialogo_invito.nome')} value={nome} onChange={(e) => setNome(e.target.value)} autoFocus fullWidth />
            <FormControlLabel control={<Switch checked={pubblico} onChange={(e) => setPubblico(e.target.checked)} />} label={t('dialogo_invito.pubblico')} />
            {!pubblico && (
              <TextField
                label={t('dialogo_invito.giorni')}
                value={giorni}
                onChange={(e) => setGiorni(e.target.value.replace(/\D/g, ''))}
                inputProps={{ inputMode: 'numeric' }}
                fullWidth
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {creato ? (
          <>
            <Button onClick={onChiudi} sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_invito.chiudi')}</Button>
            <Button onClick={() => onCopia(creato.link)} variant="contained" sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_invito.copia')}</Button>
          </>
        ) : (
          <>
            <Button onClick={onChiudi} disabled={invio} sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_invito.annulla')}</Button>
            <Button onClick={() => void crea()} disabled={invio} variant="contained" sx={{ textTransform: 'none', minHeight: 40 }}>{t('dialogo_invito.crea')}</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
